import React, { useEffect, useRef, useState } from "react";
import { SaveStatus } from "../../shared/utility/save-status";
import { KModal } from "../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../ui-kit/k-modal/k-modal-footer";
import { type KuikaButtonStatus } from "../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../ui-kit/kuika-selectbox/kuika-selectbox";
import TableEditorAlert from "./table-editor-alert";
import { buildTableEditorErrorAlert, ITableEditorAlert } from "./table-editor-errors";

// KOA-5857 — "Generate rows with AI" modal.
//
// Lives next to the row editor. Asks for a row count and an optional natural-
// language hint, then defers the actual call to the parent (which knows the
// table, schema, and how to dispatch the resulting rows into pendingChanges).
// The user reviews + saves like any other insert, so we lean hard on the
// existing batch path instead of writing a separate "AI commit" flow.

export interface IAiGenerateRowsModalProps {
  isOpen: boolean;
  tableName: string;
  // Count is constrained server-side to <=25; the picker reflects that.
  onClose: () => void;
  onSubmit: (count: number, hint: string) => Promise<void>;
}

// Round numbers cover the common "give me a few rows to look at" through
// "fill out a sprint's worth of test data" range. The server cap is 25.
const COUNT_OPTIONS = [
  { value: 3, label: "3 rows" },
  { value: 5, label: "5 rows" },
  { value: 10, label: "10 rows" },
  { value: 15, label: "15 rows" },
  { value: 25, label: "25 rows" }
];

const AiGenerateRowsModal: React.FC<IAiGenerateRowsModalProps> = (
  props: IAiGenerateRowsModalProps
): JSX.Element | null => {
  const { isOpen, tableName, onClose, onSubmit } = props;

  const [count, setCount] = useState<number>(5);
  const [hint, setHint] = useState<string>("");
  const [error, setError] = useState<ITableEditorAlert | null>(null);
  // Where Generate stands: "Generating", then "Generated" held before the dialog closes onto the
  // grid, where the new draft rows already are.
  const [generateStatus, setGenerateStatus] = useState<KuikaButtonStatus>("idle");
  const generateMachineRef = useRef<SaveStatus | null>(null);
  if (generateMachineRef.current === null) {
    generateMachineRef.current = new SaveStatus(setGenerateStatus);
  }
  const generateMachine: SaveStatus = generateMachineRef.current;
  useEffect((): (() => void) => (): void => generateMachine.dispose(), [generateMachine]);
  const isGenerating: boolean = generateStatus === "loading";

  const submit = async (): Promise<void> => {
    // A second press during the ✓ would generate a second batch behind the closing dialog.
    if (!generateMachine.canStart) return;
    generateMachine.start();
    setError(null);
    try {
      await onSubmit(count, hint);
      generateMachine.succeed(onClose);
    } catch (e: unknown) {
      generateMachine.fail();
      const alert: ITableEditorAlert = await buildTableEditorErrorAlert(e, {
        operation: "ai",
        title: "AI generation failed",
        fallbackMessage: "AI could not generate rows for this table."
      });
      setError(alert);
    }
  };

  if (!isOpen) {
    return null;
  }

  // Block all close paths (X icon, outside click, Esc) while a
  // generation request is in flight. Otherwise a user can dismiss
  // the modal, switch tasks, and get surprised when N draft inserts
  // suddenly appear in the grid minutes later. The Cancel button is
  // ALSO disabled-mid-flight (handled below); the user has to wait
  // for the request to resolve or close the tab to abort.
  const guardedClose = (): void => {
    if (isGenerating) {
      return;
    }
    onClose();
  };

  return (
    <KModal
      showModal={isOpen}
      closeModal={guardedClose}
      header={
        <span className="kuika-table-editor__cell-expander-header">
          <span className="kuika-table-editor__cell-expander-header-kicker" aria-hidden="true">
            Generate with AI
          </span>
          <span className="kuika-table-editor__cell-expander-header-title">{tableName}</span>
          <span className="kuika-table-editor__cell-expander-meta-pills">
            <span className="kuika-table-editor__cell-expander-pill">{count} rows</span>
            <span className="kuika-table-editor__cell-expander-pill">draft inserts</span>
          </span>
        </span>
      }
      width={520}
      footer={
        <KModalFooter
          onCancel={onClose}
          submitLabel="Generate"
          onSubmit={submit}
          status={generateStatus}
          statusLabels={{ loading: "Generating", success: "Generated" }}
        />
      }
    >
      <div className="kuika-table-editor__ai-modal">
        <div className="kuika-table-editor__ai-modal-field">
          <label className="kuika-table-editor__ai-modal-label">How many rows?</label>
          <KuikaSelectbox
            size="md"
            value={count}
            options={COUNT_OPTIONS}
            onChange={(next: string | number | (string | number)[] | null): void => {
              if (typeof next === "number") {
                setCount(next);
              }
            }}
          />
        </div>

        <div className="kuika-table-editor__ai-modal-field">
          <label className="kuika-table-editor__ai-modal-label">
            Hint <span className="kuika-table-editor__ai-modal-optional">(optional)</span>
          </label>
          <KuikaInput
            type="textarea"
            size="md"
            value={hint}
            placeholder='e.g. "European software companies founded after 2015"'
            onChange={(_e, val: string | number): void => setHint(String(val))}
            onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                void submit();
              }
            }}
          />
          <span className="kuika-table-editor__ai-modal-help">
            Generated rows appear as draft inserts you can review and edit. Nothing reaches the database until you save.
          </span>
        </div>

        {error && <TableEditorAlert alert={error} onDismiss={(): void => setError(null)} />}
      </div>
    </KModal>
  );
};

export default AiGenerateRowsModal;
